/**
 * Create a production.
 *
 * The screen that made the empty state a dead end by not existing. Without a
 * production there are no unit-days, no documents and no estimates — nothing
 * in the modeller is reachable — and `POST /v1/productions` had no UI at all.
 *
 * Bound to `ProductionIn`: `slug`, `name` and `timezone` are required, the
 * rest optional, `status` defaults to 'prospect' server-side. Nothing here
 * invents a field.
 *
 * This is the product's ONLY write. It is reachable only when the session
 * holds an editing key, because a control that exists and refuses makes the
 * product look broken rather than unfinished.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { modeller, PRODUCTION_STATUSES, type NewProduction as Body } from '../api';
import { PageHeader } from '../shell/Shell';
import { Note } from '../shell/provenance';
import './newproduction.css';

/** A slug is an identity: lowercase, no spaces. Derived from the name as a
 *  starting point, and freely editable — it is not a formatting of the name,
 *  it is the handle the URL and every later reference uses. */
function slugify(name: string): string {
  return name.toLowerCase().trim()
    /* Apostrophes are DROPPED, not converted: "Charlotte's Web" is
       charlottes-web, not charlotte-s-web. Everything else that is not a
       letter or digit becomes one separator. */
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
}

/** The zones this team actually shoots in, plus whatever the browser is set
 *  to. The server validates against the full IANA database, so a value not
 *  listed here is still accepted — this is a shortcut, not a whitelist. */
function zoneChoices(): string[] {
  const local = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const common = [
    'Australia/Melbourne', 'Australia/Sydney', 'Australia/Brisbane',
    'Australia/Perth', 'Australia/Adelaide', 'Pacific/Auckland',
    'America/Los_Angeles', 'America/New_York', 'Europe/London', 'UTC',
  ];
  return common.includes(local) ? common : [local, ...common];
}

export function NewProduction() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [client, setClient] = useState('');
  const [company, setCompany] = useState('');
  const [zone, setZone] = useState(zoneChoices()[0]!);
  const [status, setStatus] = useState<Body['status']>('prospect');
  const [createdBy, setCreatedBy] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const effectiveSlug = slugTouched ? slug : slugify(name);
  const ready = name.trim() !== '' && effectiveSlug !== '' && zone !== '';

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const created = await modeller.createProduction({
        slug: effectiveSlug,
        name: name.trim(),
        timezone: zone,
        status,
        /* Empty strings are not blanks the API should store. */
        client: client.trim() || null,
        production_company: company.trim() || null,
        created_by: createdBy.trim() || null,
      });
      navigate(`/modeller/productions/${created.slug}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  return (
    <>
      <PageHeader title="New production" />
      <form className="pem-form" onSubmit={submit}>
        <label className="pem-field">
          <span className="pem-label">Production name</span>
          <input value={name} autoFocus required
                 onChange={(e) => setName(e.target.value)}
                 placeholder="Charlotte's Web" />
        </label>

        <label className="pem-field">
          <span className="pem-label">Short handle</span>
          <input value={effectiveSlug} required maxLength={64}
                 onChange={(e) => { setSlugTouched(true); setSlug(slugify(e.target.value)); }} />
          <span className="pem-provenance">
            Used in links and in conversation. Lowercase, no spaces, and fixed
            once set.
          </span>
        </label>

        <div className="pem-form__pair">
          <label className="pem-field">
            <span className="pem-label">Client</span>
            <input value={client} onChange={(e) => setClient(e.target.value)}
                   placeholder="optional" />
          </label>
          <label className="pem-field">
            <span className="pem-label">Production company</span>
            <input value={company} onChange={(e) => setCompany(e.target.value)}
                   placeholder="optional" />
          </label>
        </div>

        <div className="pem-form__pair">
          <label className="pem-field">
            <span className="pem-label">Time zone</span>
            <input value={zone} required list="pem-zones"
                   onChange={(e) => setZone(e.target.value)} />
            <datalist id="pem-zones">
              {zoneChoices().map((z) => <option key={z} value={z} />)}
            </datalist>
            {/* Not cosmetic: every shoot day becomes a telemetry window
                through this, so a wrong zone moves the numbers. */}
            <span className="pem-provenance">
              Where it shoots. Every day's hours are read through this.
            </span>
          </label>
          <label className="pem-field">
            <span className="pem-label">Stage</span>
            <select value={status} onChange={(e) => setStatus(e.target.value as Body['status'])}>
              {PRODUCTION_STATUSES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </label>
        </div>

        <label className="pem-field">
          <span className="pem-label">Created by</span>
          <input value={createdBy} onChange={(e) => setCreatedBy(e.target.value)}
                 placeholder="your name — optional" />
        </label>

        <div className="pem-form__actions">
          <button type="submit" className="pem-btn" disabled={busy || !ready}>
            {busy ? 'Creating' : 'Create production'}
          </button>
          <button type="button" className="pem-btn pem-btn--quiet"
                  onClick={() => navigate('/modeller/productions')}>
            Cancel
          </button>
        </div>

        {error && <p className="pem-form__error">{error}</p>}

        <Note>
          A production holds its unit-days, its equipment and its estimates.
          Nothing is estimated yet — this only opens the job.
        </Note>
      </form>
    </>
  );
}
